import { yupResolver } from '@hookform/resolvers/yup';
import {
  useEditTransferRatesMutation,
  useGetTransferRatesQuery,
} from 'common/api/endpoints/harvestData';
import { handleError } from 'common/api/utils/handleError';
import { DataTable, DataTableProps } from 'common/components/DataTable';
import * as notifier from 'common/services/notification';
import {
  TransferRateTableRows,
  useTransferRatesTableData,
} from 'features/settings-view/hooks/useTransferRatesTableData';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ratesSchema } from 'utils/schemas/settings';
import { formatDefaultRates, toEditTransferRatesDto } from '../utils/functions';
import { AddTransferRoute } from './AddTransferRoute';

export type TransferRateEdit = Record<string, number | null>;

export const TransferRates: FC = () => {
  /**
   * Because transfer rates don't have periods and we don't care about surcharges
   * in the 'Transfer Rate' tab context, any date can be used to fetch the only
   * existing set of transfer rates by range.
   */
  const dummyDate = '2025-01-01';
  const {
    data: rates,
    isLoading,
    isFetching,
    error,
  } = useGetTransferRatesQuery({ weekStart: dummyDate });
  const [editTransfers, { isLoading: isSubmitting }] =
    useEditTransferRatesMutation();

  const methods = useForm<TransferRateEdit>({
    values: formatDefaultRates(rates || []),
    resolver: yupResolver(ratesSchema),
    mode: 'all',
  });
  const {
    getFieldState,
    reset,
    formState: { isDirty },
    watch,
  } = methods;

  const { columns, data, isEditing, setIsEditing } = useTransferRatesTableData(
    rates || [],
    watch,
    isSubmitting,
  );

  const closeForm = () => {
    reset();
    setIsEditing(false);
  };

  const onSubmit = async (data: TransferRateEdit): Promise<void> => {
    if (!isDirty) {
      // No values to save.
      closeForm();
      return;
    }

    const dto = toEditTransferRatesDto(rates || [], data, getFieldState);

    if (dto.length) {
      try {
        await editTransfers(dto).unwrap();
        notifier.showSuccessMessage('Transfer rates updated successfully.');
        closeForm();
      } catch (e) {
        handleError(e, 'Unable to edit transfer rates.');
      }
    } else {
      closeForm();
    }
  };

  const tableProps: DataTableProps<TransferRateTableRows, TransferRateEdit> = {
    columns,
    data,
    isLoading,
    isFetching,
    maxTableWidth: '900px',
    formProps: {
      methods,
      onSubmit,
      onCancel: closeForm,
      isEditing,
      setIsEditing,
      isDirty,
      isSubmitting,
    },
    FooterComponent: isEditing ? undefined : () => <AddTransferRoute />,
  };

  useEffect(() => {
    if (error) {
      handleError(error, 'Unable to fetch transfer rates.');
    }
  }, [error]);

  return <DataTable {...tableProps} />;
};
